/**
 * CartDrawer Component
 * A zero-dependency Web Component for the `<cart-drawer>` element in a Shopify theme.
 */

class CartDrawer extends HTMLElement {
  #firstFocusable;
  #lastFocusable;
  #triggerElement;
  #abortController;

  constructor() {
    super();
    this.#abortController = new AbortController();
  }

  connectedCallback() {
    // Accessibility attributes
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    if (!this.hasAttribute('aria-label')) {
      this.setAttribute('aria-label', 'Shopping cart');
    }

    // Cache elements
    this.overlay = this.querySelector('.cart-drawer__overlay');
    this.closeBtn = this.querySelector('.cart-drawer__close');
    this.itemsContainer = this.querySelector('[data-cart-items]');
    this.subtotalElement = this.querySelector('[data-cart-subtotal]');
    this.shippingBar = this.querySelector('[data-shipping-bar-progress]');
    this.shippingMessage = this.querySelector('[data-shipping-text]');
    this.upsellContainer = this.querySelector('[data-cart-upsells]');
    this.liveRegion = this.querySelector('[aria-live="polite"]');

    if (!this.liveRegion) {
      this.liveRegion = document.createElement('div');
      this.liveRegion.setAttribute('aria-live', 'polite');
      this.liveRegion.classList.add('visually-hidden', 'sr-only');
      this.appendChild(this.liveRegion);
    }

    this.bindEvents();
  }

  disconnectedCallback() {
    this.#abortController.abort();
  }

  bindEvents() {
    const { signal } = this.#abortController;

    // Listen for open events on document
    document.addEventListener('cart-drawer:open', this.open.bind(this), { signal });
    document.addEventListener('cart:item-added', this.open.bind(this), { signal });

    // Internal click events (delegated)
    this.addEventListener('click', this.handleClick.bind(this), { signal });

    // Quantity updates via input change
    this.addEventListener('change', this.handleChange.bind(this), { signal });

    // Keyboard navigation (Escape to close, Tab for focus trap)
    this.addEventListener('keydown', this.handleKeydown.bind(this), { signal });
  }

  handleClick(event) {
    // Close on overlay or close button click
    if (event.target === this.overlay || event.target.closest('.cart-drawer__close')) {
      this.close();
      return;
    }

    // Remove item
    const removeBtn = event.target.closest('.cart-drawer__remove');
    if (removeBtn) {
      event.preventDefault();
      const lineKey = removeBtn.dataset.lineItemKey || removeBtn.dataset.lineKey || removeBtn.dataset.id;
      if (lineKey) {
        this.removeItem(lineKey);
      }
      return;
    }

    // Quantity +/- buttons
    const qtyMinusBtn = event.target.closest('[data-qty-minus]');
    const qtyPlusBtn = event.target.closest('[data-qty-plus]');
    if (qtyMinusBtn || qtyPlusBtn) {
      event.preventDefault();
      const wrapper = (qtyMinusBtn || qtyPlusBtn).closest('.cart-drawer__item-quantity');
      const input = wrapper?.querySelector('[data-qty-input]');
      if (input) {
        const min = Number(input.min) || 0;
        const max = Number(input.max) || Infinity;
        const step = qtyMinusBtn ? -1 : 1;
        const newValue = Math.min(max, Math.max(min, (parseInt(input.value, 10) || 0) + step));
        input.value = newValue;
        const lineKey = input.dataset.lineItemKey || input.dataset.lineKey || input.dataset.id;
        if (lineKey) {
          this.updateQuantity(lineKey, newValue);
        }
      }
      return;
    }

    // Upsell quick add
    const quickAddBtn = event.target.closest('.cart-drawer__upsell-add');
    if (quickAddBtn) {
      event.preventDefault();
      const variantId = quickAddBtn.dataset.variantId;
      if (variantId) {
        this.quickAdd(variantId);
      }
    }
  }

  handleChange(event) {
    // Listen for direct edits to the quantity input
    const input = event.target.closest('[data-qty-input]');
    if (input) {
      const lineKey = input.dataset.lineItemKey || input.dataset.lineKey || input.dataset.id;
      if (lineKey) {
        this.updateQuantity(lineKey, input.value);
      }
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      this.close();
    } else if (event.key === 'Tab') {
      this.trapFocus(event);
    }
  }

  open(event) {
    // Save active element to restore focus on close
    this.#triggerElement = document.activeElement;

    // Add-to-cart callers send the freshly rendered drawer with the add
    // (cart/add.js `sections`). Render it BEFORE the drawer slides in, so the
    // shopper never sees the stale/"Your cart is empty" drawer for the ~1s the
    // old open-then-fetch took (2026-10-09).
    const sections = event && event.detail && event.detail.sections;
    const freshHtml = sections && sections['cart-drawer'];
    if (freshHtml) this.renderCartContent(freshHtml);

    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');

    // Lock body scroll
    document.body.style.overflow = 'hidden';

    // Fetch fresh cart HTML content (only when the caller didn't bring it)
    if (!freshHtml) this.fetchCart();
    
    // Initialize focus trap after transition
    setTimeout(() => {
      this.initFocusTrap();
    }, 100);
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Return focus to the element that opened the drawer
    if (this.#triggerElement) {
      this.#triggerElement.focus();
    }
    
    // Dispatch close event for other components
    this.dispatchEvent(new CustomEvent('cart-drawer:close', { bubbles: true }));
  }

  initFocusTrap() {
    const focusableElementsString = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusable = Array.from(this.querySelectorAll(focusableElementsString)).filter(el => {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
    });

    if (focusable.length === 0) return;
    
    this.#firstFocusable = focusable[0];
    this.#lastFocusable = focusable[focusable.length - 1];
    
    // Focus the first element (often the close button or first cart item)
    this.#firstFocusable.focus();
  }

  trapFocus(event) {
    if (!this.#firstFocusable) return;
    
    if (event.shiftKey) {
      // Shift + Tab: if on first element, wrap to last
      if (document.activeElement === this.#firstFocusable) {
        event.preventDefault();
        this.#lastFocusable.focus();
      }
    } else {
      // Tab: if on last element, wrap to first
      if (document.activeElement === this.#lastFocusable) {
        event.preventDefault();
        this.#firstFocusable.focus();
      }
    }
  }

  async fetchCart() {
    try {
      const sectionName = 'cart-drawer';
      const response = await fetch(`${window.location.pathname}?sections=${sectionName}`);
      
      if (!response.ok) throw new Error('Network response was not ok');
      
      const sections = await response.json();
      if (sections[sectionName]) {
        this.renderCartContent(sections[sectionName]);
      }
    } catch (error) {
      console.error('Failed to fetch cart section HTML:', error);
    }
  }

  renderCartContent(htmlString) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    
    // Replace items container
    const newItemsContainer = doc.querySelector('[data-cart-items]');
    if (newItemsContainer && this.itemsContainer) {
      this.itemsContainer.innerHTML = newItemsContainer.innerHTML;
    }

    // Replace subtotal
    const newSubtotal = doc.querySelector('[data-cart-subtotal]');
    if (newSubtotal && this.subtotalElement) {
      this.subtotalElement.innerHTML = newSubtotal.innerHTML;
    }

    // Sync checkout button amount
    const newCheckoutSubtotal = doc.querySelector('[data-cart-subtotal-checkout]');
    const checkoutSubtotalElement = this.querySelector('[data-cart-subtotal-checkout]');
    if (newCheckoutSubtotal && checkoutSubtotalElement) {
      checkoutSubtotalElement.innerHTML = newCheckoutSubtotal.innerHTML;
    }

    // Update global cart counts (e.g. header cart badge)
    const newCountElements = doc.querySelectorAll('[data-cart-count]');
    const existingCountElements = document.querySelectorAll('[data-cart-count]');
    if (newCountElements.length > 0 && existingCountElements.length > 0) {
      existingCountElements.forEach(el => {
        el.innerHTML = newCountElements[0].innerHTML;
      });
    }

    // Update the drawer's own item-count text (e.g. "장바구니 (3개)")
    const newCountText = doc.querySelector('[data-cart-count-text]');
    const existingCountText = this.querySelector('[data-cart-count-text]');
    if (newCountText && existingCountText) {
      existingCountText.innerHTML = newCountText.innerHTML;
    }

    // The footer (subtotal + checkout button) and the upsells block are only
    // rendered by Liquid when the cart has items. If the page loaded with an
    // empty cart they are not in the DOM at all, and the old code only toggled
    // an existing footer - so a shopper's first add-to-cart opened a drawer
    // with no checkout button (2026-09-14). Sync both blocks from the fresh
    // section HTML instead: insert when missing, replace when present, remove
    // when the cart empties.
    this.syncBlock('.cart-drawer__footer', doc);
    this.syncBlock('.cart-drawer__upsells', doc, '.cart-drawer__footer');
    this.subtotalElement = this.querySelector('[data-cart-subtotal]');
    this.upsellContainer = this.querySelector('[data-cart-upsells]');


    // Announce update for screen readers
    this.announce('Cart updated');

    // Update free shipping bar and upsells after DOM mutation
    this.updateFreeShipping();
    this.fetchUpsells(doc);
    
    // Re-initialize focus trap to account for new DOM elements
    this.initFocusTrap();
  }

  syncBlock(selector, doc, beforeSelector) {
    const panel = this.querySelector('.cart-drawer__panel');
    const existing = this.querySelector(selector);
    const fresh = doc.querySelector(selector);

    if (!fresh) {
      if (existing) existing.remove();
      return;
    }

    const node = document.importNode(fresh, true);

    if (existing) {
      // Keep whatever the shopper has typed into the order note
      const oldNote = existing.querySelector('[data-cart-note]');
      const newNote = node.querySelector('[data-cart-note]');
      if (oldNote && newNote) newNote.value = oldNote.value;
      const oldToggle = existing.querySelector('details');
      const newToggle = node.querySelector('details');
      if (oldToggle && newToggle) newToggle.open = oldToggle.open;
      existing.replaceWith(node);
      return;
    }

    const before = beforeSelector ? this.querySelector(beforeSelector) : null;
    if (before) {
      before.before(node);
    } else if (panel) {
      panel.appendChild(node);
    }
  }

  async updateQuantity(lineKey, quantity) {
    this.setLoadingState(lineKey, true);
    
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ id: lineKey, quantity: parseInt(quantity, 10) })
      });
      
      if (!response.ok) throw new Error('Error updating cart');

      const cart = await response.json();
      this.#syncHeaderCartCount(cart.item_count);
      await this.fetchCart();
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.announce('Error updating cart quantity');
    } finally {
      this.setLoadingState(lineKey, false);
    }
  }

  #syncHeaderCartCount(itemCount) {
    document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
      bubble.textContent = itemCount > 0 ? itemCount : '';
    });
  }

  async removeItem(lineKey) {
    const lineItem = this.querySelector(`[data-line-item-key="${lineKey}"]`);
    if (lineItem) {
      lineItem.classList.add('is-removing');
    }
    await this.updateQuantity(lineKey, 0);
  }

  async quickAdd(variantId) {
    const btn = this.querySelector(`[data-variant-id="${variantId}"]`);
    if (btn) btn.classList.add('is-loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      
      if (!response.ok) throw new Error('Error adding upsell item');

      await response.json();
      const cart = await (await fetch('/cart.js')).json();
      this.#syncHeaderCartCount(cart.item_count);
      await this.fetchCart();
      this.announce('Item added to cart');
    } catch (error) {
      console.error('Error adding upsell item:', error);
      this.announce('Error adding item to cart');
    } finally {
      if (btn) btn.classList.remove('is-loading');
    }
  }

  setLoadingState(lineKey, isLoading) {
    const lineItem = this.querySelector(`[data-line-item-key="${lineKey}"]`);
    if (lineItem) {
      if (isLoading) {
        lineItem.classList.add('is-loading');
        lineItem.setAttribute('aria-busy', 'true');
      } else {
        lineItem.classList.remove('is-loading');
        lineItem.removeAttribute('aria-busy');
      }
    }
  }

  announce(message) {
    if (this.liveRegion) {
      this.liveRegion.textContent = message;
    }
  }

  async updateFreeShipping() {
    if (!this.hasAttribute('data-free-shipping-threshold') || !this.shippingBar) return;
    
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      
      const threshold = parseInt(this.dataset.freeShippingThreshold, 10);
      if (isNaN(threshold) || threshold <= 0) return;
      
      const total = cart.total_price;
      let percentage = (total / threshold) * 100;
      percentage = Math.min(percentage, 100);
      
      // Update progress bar width
      this.shippingBar.style.width = `${percentage}%`;
      this.shippingBar.setAttribute('aria-valuenow', percentage);
      
      // Update message
      if (this.shippingMessage) {
        if (percentage >= 100) {
          this.shippingMessage.textContent = 'You have got free shipping!';
          this.shippingBar.classList.add('is-success');
        } else {
          const remaining = ((threshold - total) / 100).toFixed(2);
          this.shippingMessage.textContent = `You are $${remaining} away from free shipping.`;
          this.shippingBar.classList.remove('is-success');
        }
      }
    } catch (error) {
      console.error('Error updating free shipping progress:', error);
    }
  }

  async fetchUpsells(doc) {
    if (!this.upsellContainer) return;
    
    // Find first product ID in cart from the fetched document
    const firstItem = doc.querySelector('[data-product-id]');
    if (!firstItem) {
      this.upsellContainer.innerHTML = '';
      return;
    }
    
    const productId = firstItem.dataset.productId;
    if (!productId) return;
    
    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=4`);
      if (!response.ok) throw new Error('Failed to fetch recommendations');
      
      const data = await response.json();
      if (data.products && data.products.length > 0) {
        this.renderUpsells(data.products);
      } else {
        this.upsellContainer.innerHTML = '';
      }
    } catch(error) {
      console.error('Failed to fetch upsells', error);
    }
  }

  renderUpsells(products) {
    if (!this.upsellContainer) return;

    let html = '<div class="cart-drawer__upsells-list">';
    products.forEach(product => {
      // Use first available variant
      const variant = product.variants.find(v => v.available) || product.variants[0];
      const price = (variant.price / 100).toFixed(2);
      const img = product.featured_image 
        ? `<img src="${product.featured_image}" alt="${product.title}" width="60" height="60" loading="lazy">` 
        : '<div class="cart-drawer__upsell-placeholder"></div>';

      html += `
        <div class="cart-drawer__upsell-item">
          <div class="cart-drawer__upsell-image">${img}</div>
          <div class="cart-drawer__upsell-info">
            <h4 class="cart-drawer__upsell-title">${product.title}</h4>
            <span class="cart-drawer__upsell-price">$${price}</span>
          </div>
          <button type="button" class="cart-drawer__upsell-add button button--small" data-variant-id="${variant.id}">
            Add <span class="visually-hidden">${product.title}</span>
          </button>
        </div>
      `;
    });
    html += '</div>';
    
    this.upsellContainer.innerHTML = html;
  }
}

customElements.define('cart-drawer', CartDrawer);
